import type { LatLng, RoundAnswer, RoundScore } from "../types";

type ReplayEntry = {
  guess: LatLng | null;
  answer: LatLng;
  score: RoundScore;
  round: RoundAnswer;
};

type ReplayArgs = {
  title: string;
  entries: ReplayEntry[];
  totalScore: number;
};

const FRAME_RATE = 30;
const ROUND_DURATION_MS = 2000;
const FINAL_HOLD_MS = 1500;

/**
 * Renders a short WebM clip animating the round-by-round map reveal with score ticking up.
 * Returns the resulting Blob; falls back to null if MediaRecorder is unsupported.
 */
export async function renderReplayVideo(args: ReplayArgs): Promise<Blob | null> {
  if (typeof MediaRecorder === "undefined") {
    return null;
  }

  const width = 1080;
  const height = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const stream = canvas.captureStream(FRAME_RATE);
  const recorder = new MediaRecorder(stream, { mimeType: pickMimeType() });
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  const stopped = new Promise<void>((resolve) => {
    recorder.addEventListener("stop", () => resolve(), { once: true });
  });

  recorder.start();
  const startTime = performance.now();
  const totalDuration = args.entries.length * ROUND_DURATION_MS + FINAL_HOLD_MS;

  await new Promise<void>((resolve) => {
    const tick = () => {
      const elapsed = performance.now() - startTime;
      drawFrame(ctx, args, elapsed, width, height);
      if (elapsed >= totalDuration) {
        resolve();
        return;
      }

      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });

  recorder.stop();
  await stopped;
  return new Blob(chunks, { type: chunks[0] instanceof Blob ? (chunks[0] as Blob).type : "video/webm" });
}

function drawFrame(
  ctx: CanvasRenderingContext2D,
  args: ReplayArgs,
  elapsed: number,
  width: number,
  height: number
): void {
  const bg = ctx.createLinearGradient(0, 0, width, height);
  bg.addColorStop(0, "#0f172a");
  bg.addColorStop(1, "#020617");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = "#7dd3fc";
  ctx.font = "700 28px Inter, system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("CHRONOSCAPE", 60, 80);

  ctx.fillStyle = "#f8fafc";
  ctx.font = "800 56px Inter, system-ui, sans-serif";
  ctx.fillText(args.title, 60, 140);

  const mapTop = 200;
  const mapHeight = height - mapTop - 160;
  const mapBox = { x: 60, y: mapTop, w: width - 120, h: mapHeight };
  drawMap(ctx, mapBox);

  const completed = Math.min(args.entries.length, Math.floor(elapsed / 2000));
  const currentRound = Math.min(args.entries.length - 1, Math.floor(elapsed / 2000));
  const phase = (elapsed % 2000) / 2000;

  let runningScore = 0;
  for (let i = 0; i < completed; i += 1) {
    const entry = args.entries[i];
    if (!entry) continue;
    runningScore += entry.score.roundScore;
    drawPins(ctx, mapBox, entry, 1);
  }

  if (currentRound < args.entries.length && completed === currentRound) {
    const entry = args.entries[currentRound];
    if (entry) {
      drawPins(ctx, mapBox, entry, phase);
      runningScore += Math.round(entry.score.roundScore * Math.min(1, phase * 1.4));
    }
  }

  ctx.fillStyle = "#7dd3fc";
  ctx.font = "800 88px Inter, system-ui, sans-serif";
  ctx.textAlign = "right";
  ctx.fillText(runningScore.toLocaleString(), width - 60, height - 60);
  ctx.fillStyle = "rgba(248,250,252,0.55)";
  ctx.font = "500 22px Inter, system-ui, sans-serif";
  ctx.fillText("score", width - 60, height - 30);

  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(248,250,252,0.65)";
  ctx.font = "500 24px Inter, system-ui, sans-serif";
  ctx.fillText(`Round ${Math.min(args.entries.length, currentRound + 1)} / ${args.entries.length}`, 60, height - 60);
}

function drawMap(ctx: CanvasRenderingContext2D, box: { x: number; y: number; w: number; h: number }): void {
  ctx.fillStyle = "rgba(15,23,42,0.7)";
  ctx.fillRect(box.x, box.y, box.w, box.h);

  ctx.strokeStyle = "rgba(125,211,252,0.16)";
  ctx.lineWidth = 1;
  for (let lng = -180; lng <= 180; lng += 30) {
    const px = box.x + ((lng + 180) / 360) * box.w;
    ctx.beginPath();
    ctx.moveTo(px, box.y);
    ctx.lineTo(px, box.y + box.h);
    ctx.stroke();
  }
  for (let lat = -90; lat <= 90; lat += 30) {
    const py = box.y + ((90 - lat) / 180) * box.h;
    ctx.beginPath();
    ctx.moveTo(box.x, py);
    ctx.lineTo(box.x + box.w, py);
    ctx.stroke();
  }

  ctx.fillStyle = "rgba(56,189,248,0.10)";
  const continents: Array<[number, number, number, number]> = [
    [-170, 13, -52, 84],
    [-90, -60, -34, 13],
    [-25, 35, 45, 72],
    [-20, -36, 52, 38],
    [25, -12, 180, 82],
    [110, -50, 180, 0],
    [-180, -90, 180, -60]
  ];
  for (const [minLng, minLat, maxLng, maxLat] of continents) {
    const x = box.x + ((minLng + 180) / 360) * box.w;
    const y = box.y + ((90 - maxLat) / 180) * box.h;
    const w = ((maxLng - minLng) / 360) * box.w;
    const h = ((maxLat - minLat) / 180) * box.h;
    ctx.fillRect(x, y, w, h);
  }
}

function drawPins(
  ctx: CanvasRenderingContext2D,
  box: { x: number; y: number; w: number; h: number },
  entry: ReplayEntry,
  phase: number
): void {
  const answerX = box.x + ((entry.answer.lng + 180) / 360) * box.w;
  const answerY = box.y + ((90 - entry.answer.lat) / 180) * box.h;

  if (entry.guess) {
    const guessX = box.x + ((entry.guess.lng + 180) / 360) * box.w;
    const guessY = box.y + ((90 - entry.guess.lat) / 180) * box.h;
    ctx.fillStyle = "#7dd3fc";
    ctx.beginPath();
    ctx.arc(guessX, guessY, 6, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = `rgba(248,250,252,${0.2 + 0.4 * phase})`;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(guessX, guessY);
    const midX = guessX + (answerX - guessX) * Math.min(1, phase);
    const midY = guessY + (answerY - guessY) * Math.min(1, phase);
    ctx.lineTo(midX, midY);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  if (phase >= 0.65) {
    ctx.fillStyle = "#fb923c";
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(answerX, answerY, 6 + (1 - phase) * 14, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

function pickMimeType(): string {
  const candidates = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      return candidate;
    }
  }
  return "video/webm";
}
